import React, { useCallback, useMemo, useState } from 'react';
import { useController } from 'react-hook-form';
import cronstrue from 'cronstrue';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import Toggle from '@amzn/meridian/toggle';
import { useTranslation } from 'src/arb-wrapper';
import { FEATURE_FLAGS } from 'src/constants';
import { isFeatureEnabled } from 'src/feature-flags';
import { FormCronInput } from 'src/components/campaigns/forms/FormCronInput';
import type { FormRhfControllerProps } from './types';

interface FormScheduleProps extends FormRhfControllerProps {}

export type Frequency = 'daily' | 'weekly' | 'monthly';

export interface ParsedSchedule {
  frequency: Frequency;
  hours: number[];
  minute: number;
  daysOfWeek: number[];
  daysOfMonth: number[];
}

const DAYS_OF_WEEK = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
] as const;

export const parseCron = (cron: string): ParsedSchedule => {
  const defaults: ParsedSchedule = { frequency: 'weekly', hours: [16], minute: 0, daysOfWeek: [1], daysOfMonth: [1] };
  if (!cron) return defaults;
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return defaults;
  const [min, hr, dom, , dow] = parts;
  const minute = parseInt(min, 10);
  const hours = hr.split(',').map(h => parseInt(h, 10)).filter(h => !isNaN(h));
  if (isNaN(minute) || !hours.length) return defaults;

  if (dow !== '*' && dom === '*') {
    const daysOfWeek = dow.split(',').map(d => parseInt(d, 10)).filter(d => !isNaN(d));
    return { frequency: 'weekly', hours, minute, daysOfWeek: daysOfWeek.length ? daysOfWeek : [1], daysOfMonth: [1] };
  }
  if (dom !== '*') {
    const daysOfMonth = dom.split(',').map(d => parseInt(d, 10)).filter(d => !isNaN(d));
    return { frequency: 'monthly', hours, minute, daysOfWeek: [1], daysOfMonth: daysOfMonth.length ? daysOfMonth : [1] };
  }
  return { frequency: 'daily', hours, minute, daysOfWeek: [1], daysOfMonth: [1] };
};

export const toCron = (s: ParsedSchedule): string => {
  const hrs = s.hours.sort((a, b) => a - b).join(',');
  switch (s.frequency) {
    case 'daily': return `${s.minute} ${hrs} * * *`;
    case 'weekly': return `${s.minute} ${hrs} * * ${s.daysOfWeek.sort((a, b) => a - b).join(',')}`;
    case 'monthly': return `${s.minute} ${hrs} ${s.daysOfMonth.sort((a, b) => a - b).join(',')} * *`;
  }
};

/** Returns the browser's current UTC offset in whole hours (e.g. -8 for PST, +5.5 rounded to +6). */
export const getLocalOffsetHours = (): number => {
  return -Math.round(new Date().getTimezoneOffset() / 60);
};

/**
 * Converts a UTC hour to local hour and returns the day offset (-1, 0, or +1).
 * E.g. UTC hour 3 in UTC-8 → local hour 19, dayOffset -1 (previous day).
 */
export const utcHourToLocal = (utcHour: number): { hour: number; dayOffset: number } => {
  const offset = getLocalOffsetHours();
  const localHour = utcHour + offset;
  if (localHour < 0) return { hour: localHour + 24, dayOffset: -1 };
  if (localHour >= 24) return { hour: localHour - 24, dayOffset: 1 };
  return { hour: localHour, dayOffset: 0 };
};

/**
 * Converts a local hour to UTC hour and returns the day offset (-1, 0, or +1).
 * E.g. local hour 23 in UTC-8 → UTC hour 7, dayOffset +1 (next day).
 */
export const localHourToUtc = (localHour: number): { hour: number; dayOffset: number } => {
  const offset = getLocalOffsetHours();
  const utcHour = localHour - offset;
  if (utcHour < 0) return { hour: utcHour + 24, dayOffset: -1 };
  if (utcHour >= 24) return { hour: utcHour - 24, dayOffset: 1 };
  return { hour: utcHour, dayOffset: 0 };
};

/** Converts an array of UTC hours to local hours (convenience wrapper). */
export const utcHoursToLocal = (utcHours: number[]): number[] =>
  utcHours.map(h => utcHourToLocal(h).hour);

/** Converts an array of local hours to UTC hours (convenience wrapper). */
export const localHoursToUtc = (localHours: number[]): number[] =>
  localHours.map(h => localHourToUtc(h).hour);

/** Shifts days of week by an offset, wrapping around 0-6. */
const shiftDaysOfWeek = (days: number[], offset: number): number[] =>
  days.map(d => ((d + offset) % 7 + 7) % 7);

const FormSchedulePicker: React.FC<{ control: FormRhfControllerProps['control']; name: string }> = ({ control, name }) => {
  const { t } = useTranslation();
  const [useLocalTime, setUseLocalTime] = useState(false);
  const { field } = useController({ control, name, rules: { required: true } });
  const cronValue = field.value as string;
  const schedule = useMemo(() => parseCron(cronValue), [cronValue]);

  /** Hours to display — converted to local when toggle is on. */
  const displayHours = useMemo(
    () => useLocalTime ? utcHoursToLocal(schedule.hours) : schedule.hours,
    [schedule.hours, useLocalTime],
  );

  /** Day offset when converting the first hour — used to shift displayed days. */
  const dayOffset = useMemo(() => {
    if (!useLocalTime || !schedule.hours.length) return 0;
    return utcHourToLocal(schedule.hours[0]).dayOffset;
  }, [schedule.hours, useLocalTime]);

  /** Days of week to display — shifted when local time crosses a day boundary. */
  const displayDaysOfWeek = useMemo(
    () => useLocalTime ? shiftDaysOfWeek(schedule.daysOfWeek, dayOffset) : schedule.daysOfWeek,
    [schedule.daysOfWeek, dayOffset, useLocalTime],
  );

  const summary = useMemo(() => {
    try {
      const text = cronstrue.toString(cronValue, { verbose: true });
      return `${text} (UTC)`;
    } catch {
      return '';
    }
  }, [cronValue]);

  const update = useCallback(
    (patch: Partial<ParsedSchedule>) => { field.onChange(toCron({ ...schedule, ...patch })); },
    [field, schedule],
  );

  const handleHoursChange = useCallback(
    (v: string | string[]) => {
      const selected = (Array.isArray(v) ? v : [v]).map(h => parseInt(h, 10));
      if (!selected.length) return;
      if (!useLocalTime) {
        update({ hours: selected });
        return;
      }
      const converted = selected.map(h => localHourToUtc(h));
      const utcHours = converted.map(c => c.hour);
      const newDayOffset = converted[0].dayOffset;
      const patch: Partial<ParsedSchedule> = { hours: utcHours };
      // Preserve the user's intended local day when the hour change crosses a
      // day boundary. displayedDay = utcDay + dayOffset (UTC→local shift of
      // the old hour). newUtcDay = displayedDay + newDayOffset (local→UTC shift
      // of the new hour). Combined: newUtcDay = utcDay + dayOffset + newDayOffset.
      const dayShift = dayOffset + newDayOffset;
      if (dayShift !== 0 && schedule.frequency === 'weekly') {
        patch.daysOfWeek = shiftDaysOfWeek(schedule.daysOfWeek, dayShift);
      }
      update(patch);
    },
    [update, useLocalTime, schedule.frequency, schedule.daysOfWeek, schedule.daysOfMonth, dayOffset],
  );

  const handleDaysOfWeekChange = useCallback(
    (v: string | string[]) => {
      const days = (Array.isArray(v) ? v : [v]).map(d => parseInt(d, 10));
      if (!days.length) return;
      // When in local time mode, reverse the day offset to store UTC days
      const utcDays = useLocalTime ? shiftDaysOfWeek(days, -dayOffset) : days;
      update({ daysOfWeek: utcDays });
    },
    [update, useLocalTime, dayOffset],
  );

  const hourOptions = useMemo(
    () => Array.from({ length: 24 }, (_, i) => ({ label: `${String(i).padStart(2, '0')}:00`, value: String(i) })),
    [],
  );

  const dayOfMonthOptions = useMemo(
    () => Array.from({ length: 31 }, (_, i) => ({ label: String(i + 1), value: String(i + 1) })),
    [],
  );

  return (
    <Column spacing="300">
      <Row alignmentHorizontal="justify" alignmentVertical="center">
        <Text type="b300">{t('form_program_refresh_schedule')}*</Text>
        <Toggle
          checked={useLocalTime}
          data-testid="schedule-local-time-toggle"
          onChange={(checked: boolean) => { setUseLocalTime(checked); }}
        >
          {t('form_schedule_local_time')}
        </Toggle>
      </Row>
      <Row spacing="300">
        <div style={{ flex: 1 }}>
          <Text color="secondary" type="b200">{t('form_schedule_frequency')}</Text>
          <Select
            value={schedule.frequency}
            onChange={(v: string) => { update({ frequency: v as Frequency }); }}
          >
            <SelectOption label={t('form_schedule_daily')} value="daily" />
            <SelectOption label={t('form_schedule_weekly')} value="weekly" />
            <SelectOption label={t('form_schedule_monthly')} value="monthly" />
          </Select>
        </div>

        {schedule.frequency === 'weekly' && (
          <div style={{ flex: 1 }}>
            <Text color="secondary" type="b200">{t('form_schedule_day_of_week')}</Text>
            <Select
              value={displayDaysOfWeek.map(String)}
              onChange={handleDaysOfWeekChange}
            >
              {DAYS_OF_WEEK.map((day, i) => (
                <SelectOption key={i} label={day} value={String(i)} />
              ))}
            </Select>
          </div>
        )}

        {schedule.frequency === 'monthly' && (
          <div style={{ flex: 1 }}>
            <Text color="secondary" type="b200">{t('form_schedule_day_of_month')}</Text>
            <Select
              value={schedule.daysOfMonth.map(String)}
              onChange={(v: string | string[]) => {
                const days = (Array.isArray(v) ? v : [v]).map(d => parseInt(d, 10));
                if (days.length) update({ daysOfMonth: days });
              }}
            >
              {dayOfMonthOptions.map(({ label, value }) => (
                <SelectOption key={value} label={label} value={value} />
              ))}
            </Select>
          </div>
        )}

        <div style={{ flex: 1 }}>
          <Text color="secondary" type="b200">{useLocalTime ? t('form_schedule_time_local') : t('form_schedule_time_utc')}</Text>
          <Select
            value={displayHours.map(String)}
            onChange={handleHoursChange}
          >
            {hourOptions.map(({ label, value }) => (
              <SelectOption key={value} label={label} value={value} />
            ))}
          </Select>
        </div>
      </Row>
      {summary && <Text color="secondary" type="b200">{summary}</Text>}
    </Column>
  );
};

export const FormSchedule: React.FC<FormScheduleProps> = (props) => {
  const { control, name: namePrefix } = props;
  const { t } = useTranslation();
  const fieldName = `${namePrefix}.refreshScheduleCron`;

  if (isFeatureEnabled(FEATURE_FLAGS.ENABLE_BLOCKLIST)) {
    return <FormSchedulePicker control={control} name={fieldName} />;
  }

  return (
    <FormCronInput
      control={control}
      data-testid={`${namePrefix}-cron-ipnut`}
      label={t('form_program_refresh_schedule')}
      name={fieldName}
      placeholder={t('form_program_refresh_schedule_placeholder')}
      required
    />
  );
};
